import NiceModal from '@ebay/nice-modal-react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';

import AddressInput from 'components/field/AddressInput';
import Button from 'components/field/Button';
import StringInput from 'components/field/StringInput';
import SwitchInput from 'components/field/SwitchInput';
import TextArea from 'components/field/TextArea';
import FormItem from 'components/form/FormItem';
import Card from 'components/global/Card';
import ConfirmationModal from 'components/global/ConfirmationModal';
import Typography from 'components/global/Typography';

import useAlert from 'hooks/useAlert';

import { openAxios } from 'services/axios';
import { OPEN } from 'services/consts';

import AppLogger from 'utils/AppLogger';

import { SaveButtonDesign } from './styled/AddressVerification1.styled';
import * as S from './styled/AddressVerification2.styled';

export const addressVerification2DefaultValues = [
	{
		type: 'color',
		value: '#f5f5f5',
		name: 'pageBackgroundColor'
	},
	{
		type: 'color',
		value: '#FFFFFF',
		name: 'navigationBackgroundColor'
	},
	{
		type: 'color',
		value: '#1bc487',
		name: 'saveButtonColor'
	},
	{
		type: 'color',
		value: '#3f51b5',
		name: 'getLocationButtonColor'
	},
	{
		type: 'color',
		value: '#ffffff',
		name: 'formBackgroundColor'
	},
	{
		type: 'color',
		value: '#000000',
		name: 'formTextColor'
	},

	{
		type: 'color',
		value: '#000000',
		name: 'infoTextColor'
	},

	{
		type: 'color',
		value: '#000000',
		name: 'headerTextColor'
	},

	{
		type: 'boolean',
		value: true,
		name: 'showOrderNumber'
	},
	{
		type: 'boolean',
		value: true,
		name: 'adressDisabled'
	},
	{
		type: 'boolean',
		value: true,
		name: 'showCustomerAddress'
	},
	{
		type: 'boolean',
		value: true,
		name: 'showDeliveryNote'
	},
	{
		type: 'boolean',
		value: false,
		name: 'showAdvertisementOnBottom'
	},
	{
		type: 'boolean',
		value: false,
		name: 'showAdvertisementOnTop'
	}
];

/**
 * @author Burak Ak
 */

const AddressVerification2 = (props) => {
	const schema = useMemo(
		() =>
			yup.object().shape({
				address: yup.object().shape({
					text: yup.string().nullable().required(),
					countryName: yup.string().nullable(),
					stateName: yup.string().nullable(),
					cityName: yup.string().nullable(),
					districtName: yup.string().nullable(),
					zipCode: yup.string().nullable(),
					lat: yup.number().min(-90).max(90).nullable(),
					lng: yup.number().min(-180).max(180).nullable()
				}),
				deliveryNote: yup.string().nullable()
			}),
		[]
	);

	const { orderNumber, useForAllAddress, address, deliveryNote, isMobile, propJson } = props;

	const [memoizedAddress, setMemoizedAddress] = useState(false);
	const [submitCountryNotMatch] = useState(false);
	const [loading, setLoading] = useState(false);
	const { t } = useTranslation();

	const { showError, showSuccess } = useAlert();

	const formMethods = useForm({
		resolver: yupResolver(schema)
	});
	const {
		handleSubmit,
		reset,
		watch,
		getValues,
		formState: { isSubmitSuccessful }
	} = formMethods;

	const afterConfirmation = async (data) => {
		setLoading(true);
		const derivedAddreess = { ...data.address };

		try {
			const response = await openAxios.post(OPEN.SMS.UPDATE_ADDRESS_VERIFICATION_SMS_DETAILS, {
				otoOrderID: props.otoOrderID,
				address: {
					...derivedAddreess,
					postCode: derivedAddreess.zipCode
				},
				deliveryNote: data.deliveryNote,
				useForAllAddress: data.useForAllAddress
			});

			if (response.status === 200) {
				showSuccess();
				setMemoizedAddress(data?.address?.text);
			} else {
				AppLogger.error(response);
			}
		} catch (error) {
			if (error.response?.data.code === 'error.SMS003') {
				showError({
					message: 'errorMsg.couldNotUpdateForThisStatus'
				});
			} else if (error.response?.data.code === 'error.SMS004') {
				showError({
					message: 'errorMsg.thisAddressIsAlreadyConfirmedByUser'
				});
			} else {
				AppLogger.error(error);
				showError();
			}
		} finally {
			setLoading(false);
		}
	};

	const onSubmit = async (data) => {
		if (submitCountryNotMatch) {
			showError({
				message: t('errorMsg.countryNotMatch', {
					country: address?.countryName && t(`country.${address.countryName}`)
				})
			});
			return;
		}

		NiceModal.show(ConfirmationModal, {
			title: 'confirmDeliveryLocation',
			subTitle: (
				<Trans
					i18nKey="confirmDeliveryLocationSubTitle"
					values={{
						address: getValues('address.text')
					}}
				/>
			),
			rejectLabel: 'cancel',
			confirmLabel: 'letsProceed',
			lottieName: 'info-animated-icon',
			yesAction: () => afterConfirmation(data)
		});
	};

	// when sms layout fetched we are filling form
	useEffect(() => {
		reset({
			address: {
				text: address?.text,
				lat: address?.latitude,
				lng: address?.longitude,
				zipCode: address?.postCode,
				cityName: address?.cityName,
				countryName: address?.countryName,
				stateName: address?.stateName,
				districtName: address?.districtName
			},
			useForAllAddress,
			deliveryNote
		});
	}, [address, deliveryNote, reset, useForAllAddress]);

	const defaultColSpan = {
		md: 12
	};

	return (
		<FormProvider {...formMethods}>
			<S.Container className={isMobile ? 'mobileView' : ''}>
				<Card
					fullHeight
					border={0}
					shadowed
					style={{
						backgroundColor: propJson?.formBackgroundColor,
						flex: 3
					}}
					className="mt-2">
					<Typography className="mb-3" fontWeight="500" style={{ color: propJson?.headerTextColor }}>
						selectLocation
					</Typography>

					<AddressInput
						showButton={false}
						name={'address'}
						disabled={propJson?.adressDisabled}
						restrictions={
							address && {
								country: {
									isNotDifferent: true,
									code: address?.countryName
								}
							}
						}
						withMap={true}
						labelColor={propJson?.formTextColor}
					/>
				</Card>

				<Card
					border={0}
					shadowed
					className="mt-2"
					fullHeight
					style={{
						backgroundColor: propJson?.formBackgroundColor,
						color: propJson?.formTextColor
					}}>
					{propJson?.showOrderNumber && (
						<div style={{ borderBottom: '1px dotted #ccc' }} className="pb-3">
							<Typography
								fontWeight="700"
								className="mb-2"
								style={{ color: propJson?.formTextColor, background: propJson?.formBackgroundColor }}>
								orderNumber
							</Typography>
							<Typography
								style={{ color: propJson?.formTextColor, background: propJson?.formBackgroundColor }}>
								{orderNumber}
							</Typography>
						</div>
					)}

					{propJson?.showCustomerAddress && (
						<div className="mt-4">
							<Typography
								fontWeight="700"
								className="mb-2"
								style={{ color: propJson?.formTextColor, background: propJson?.formBackgroundColor }}>
								currentLocation
							</Typography>
							<Typography
								noWrap
								style={{ color: propJson?.formTextColor, background: propJson?.formBackgroundColor }}>
								{isSubmitSuccessful ? memoizedAddress : address?.text}
							</Typography>
						</div>
					)}

					<div style={{ display: 'flex', flexDirection: 'column' }}>
						<FormItem
							label="country"
							colSpan={defaultColSpan}
							color={propJson?.formTextColor}
							disabled
							style={{ padding: '1rem 0' }}>
							<StringInput value={watch('address.countryName')} disabled />
						</FormItem>
						<FormItem
							label="state"
							colSpan={defaultColSpan}
							color={propJson?.formTextColor}
							style={{ padding: '1rem 0' }}>
							<StringInput value={watch('address.stateName')} disabled />
						</FormItem>
						<FormItem
							label="city"
							colSpan={defaultColSpan}
							color={propJson?.formTextColor}
							style={{ padding: '1rem 0' }}>
							<StringInput value={watch('address.cityName')} disabled />
						</FormItem>
						<FormItem
							label="district"
							colSpan={defaultColSpan}
							color={propJson?.formTextColor}
							style={{ padding: '1rem 0' }}>
							<StringInput value={watch('address.districtName')} disabled />
						</FormItem>

						<FormItem
							label="zipCode"
							colSpan={defaultColSpan}
							color={propJson?.formTextColor}
							style={{ padding: '1rem 0' }}>
							<StringInput value={watch('address.zipCode')} disabled />
						</FormItem>
					</div>

					<FormItem label="useThisAddressForOrders" direction="row-reverse" color={propJson?.formTextColor}>
						<SwitchInput name="useForAllAddress" className="mb-2" />
					</FormItem>

					{propJson?.showDeliveryNote && (
						<TextArea
							placeholder="enterYourNote"
							name="deliveryNote"
							label="deliveryNote"
							rows="8"
							style={{
								marginLeft: 0,
								marginRight: 0,
								height: 'unset',
								backgroundColor: propJson?.formBackgroundColor,
								color: propJson?.formTextColor
							}}
							disabled={isSubmitSuccessful}
						/>
					)}
					<SaveButtonDesign saveButtonColor={propJson?.saveButtonColor} className="mt-3">
						<Button
							onClick={handleSubmit(onSubmit, (errors) => {
								AppLogger.log(errors);
							})}
							size="large"
							label="confirmDeliveryLocation"
							loading={loading}
							disabled={isSubmitSuccessful}
						/>
					</SaveButtonDesign>
				</Card>
			</S.Container>
		</FormProvider>
	);
};

export default AddressVerification2;

AddressVerification2.defaultProps = {
	headerTextColor: '#000000',
	formBackgroundColor: '#ffffff',
	formTextColor: '#000000',
	getLocationButtonColor: '#3f51b5',
	saveButtonColor: '#1bc487',
	infoBackgroundColor: '#f39c12',
	infoTextColor: '#ffffff',
	showCustomerAddress: true,
	showOrderNumber: true
};
